import type {
  AccessState,
  BillingCadence,
  PlanKey,
  PublicCredentialLimits,
  PublicCredentialRisk,
  WorkspaceCapabilitySnapshot,
  WorkspaceRole,
} from "@mph/launch-control";
import type { CustomHttpManifest } from "@mph/custom-http";
import {
  TEAM_SKILLS_V1_UPLOAD_MAX_FILES,
  TEAM_SKILLS_V1_UPLOAD_MAX_PACKAGE_BYTES,
  type TeamSkillCapabilityDiff,
  type TeamSkillVersionComparison,
} from "@mph/team-skills";

export type { BillingCadence, PlanKey, WorkspaceRole } from "@mph/launch-control";

export interface WorkspaceSummary {
  id: string;
  name: string;
  role: WorkspaceRole;
  plan?: PlanKey;
  capabilities: WorkspaceCapabilitySnapshot;
  iconUrl?: string;
}

export interface TeamSkillsDeviceLinkReview {
  schemaVersion: number;
  protocolVersion: string;
  linkId: string;
  workspaceId: string;
  deviceId: string;
  deviceLabel: string;
  requestedOperations: Array<"skill:view" | "skill:publish">;
  approvingRole: WorkspaceRole;
  expiresAt: string;
}

export function reviewTeamSkillsDeviceLink(
  linkId: string,
  approvalCode: string,
  workspaceId: string,
): Promise<TeamSkillsDeviceLinkReview> {
  return accountRequest(`/api/team-skills/device/v1/links/${encodeURIComponent(linkId)}/review`, {
    method: "POST",
    body: JSON.stringify({ approvalCode, workspaceId }),
  });
}

export function approveTeamSkillsDeviceLink(
  linkId: string,
  approvalCode: string,
  workspaceId: string,
): Promise<{ callbackUrl: string }> {
  return accountRequest(`/api/team-skills/device/v1/links/${encodeURIComponent(linkId)}/approve`, {
    method: "POST",
    body: JSON.stringify({ approvalCode, workspaceId }),
  });
}

export interface WorkspaceOverview {
  workspace: Omit<WorkspaceSummary, "capabilities"> & { kind: "personal" | "team" };
  plan: {
    effective: PlanKey;
    desired: PlanKey | null;
    accessState: AccessState;
    billingReady: boolean;
    billingCadence: BillingCadence | null;
    catalogVersion: string;
  };
  capabilities: WorkspaceCapabilitySnapshot;
  limits: {
    liveApps: number;
    creatorSeats: number;
    monthlyVisits: number;
    customDomains: number;
  };
  domainUsage: {
    used: number;
    limit: number;
  };
  projects: Array<{
    id: string;
    name: string;
    description?: string;
    customDomain?: string;
    ownerName: string;
    canDelete: boolean;
    status: "live" | "offline" | "draft";
    visibility: "public" | "password" | "workspace";
    updatedAt: string;
    capabilities?: WorkspaceCapabilitySnapshot;
  }>;
  members: Array<{
    id: string;
    name: string;
    email: string;
    role: WorkspaceRole;
  }>;
  teamDraft: TeamDraftInput | null;
  usage: Array<{
    meter: string;
    label: string;
    configured: boolean;
    used: number;
    limit: number;
    periodStart?: string;
    periodEnd?: string;
  }>;
  audit: Array<{ id: string; action: string; actorName: string; occurredAt: string }>;
}

export interface TeamDraftInput {
  name: string;
  cadence: BillingCadence;
  members: Array<{ email: string; role: "creator" | "viewer" }>;
}

export type TeamSkillLifecycle = "active" | "disabled" | "removed";
export type TeamSkillClassification = "content_only" | "privileged";

export interface TeamSkillLibraryItem {
  skillId: string;
  canonicalName: string;
  displayName: string;
  description: string;
  presentationKind?: "skill" | "collection";
  collectionAreas?: string[];
  tags?: string[];
  lifecycle: TeamSkillLifecycle;
  locked: boolean;
  stateGeneration: number;
  updatedAt: string;
  liveVersion: null | {
    versionId: string;
    versionNumber: number;
    classification: TeamSkillClassification;
    contributor: { id: string; name: string };
    lastUpdatedBy: { id: string; name: string };
    releasedAt: string;
    releaseReason: string;
    releaseNotes: string | null;
  };
  latestSharedVersion: null | {
    versionId: string;
    versionNumber: number;
    classification: TeamSkillClassification;
    contributor: { id: string; name: string };
    createdAt: string;
  };
  pendingDrafts: number;
  pendingApproval: null | {
    approvalId: string;
    versionId: string;
    versionNumber: number;
    requestedBy: { id: string; name: string };
    requestedAt: string;
  };
  health: { managedDevices: number; currentDevices: number; failedDevices: number; pausedDevices: number };
}

export interface TeamSkillsLibraryResult {
  workspaceId: string;
  role: WorkspaceRole;
  catalogVersion: string;
  auditCursor: number;
  tags?: string[];
  skills: TeamSkillLibraryItem[];
}

export interface TeamSkillHistoryResult {
  workspaceId: string;
  role: WorkspaceRole;
  catalogVersion: string;
  skill: TeamSkillLibraryItem;
  versions: Array<{
    versionId: string;
    versionNumber: number;
    baseVersionId: string | null;
    contributor: { id: string; name: string };
    classification: TeamSkillClassification;
    capability: Record<string, unknown>;
    releaseNotes: string | null;
    createdAt: string;
    isLive: boolean;
    hasBeenReleased: boolean;
    releaseReason: string | null;
    changeSummary: string;
    fileCount: number;
    files: Array<{ ordinal: number; path: string; byteLength: number; mode: string; sha256: string }>;
  }>;
  events: Array<{
    releaseEventId: string;
    stateGeneration: number;
    reason: string;
    actor: { id: string; name: string };
    createdAt: string;
  }>;
  pagination: {
    versions: { nextCursor: number | null };
    events: { nextCursor: number | null };
  };
}

export interface TeamSkillVersionComparisonResult {
  workspaceId: string;
  skillId: string;
  role: WorkspaceRole;
  catalogVersion: string;
  baseVersion: { versionId: string; versionNumber: number } | null;
  version: { versionId: string; versionNumber: number };
  files: TeamSkillVersionComparison;
  capabilityChanges: TeamSkillCapabilityDiff | null;
}

export type TeamSkillOwnerAction = "approve" | "restore" | "re-enable" | "remove";

export interface TeamSkillOwnerStepUpResult {
  authorizationUrl: string;
  expiresAt: number;
  binding: {
    workspaceId: string;
    skillId: string;
    action: TeamSkillOwnerAction;
    versionId: string | null;
    expectedStateGeneration: number;
  };
}

export const AI_PROVIDERS = ["anthropic", "google", "openai"] as const;
export type AiProvider = (typeof AI_PROVIDERS)[number];

export function isAiProvider(value: FormDataEntryValue | null): value is AiProvider {
  return typeof value === "string" && AI_PROVIDERS.includes(value as AiProvider);
}

export function accountErrorMessage(cause: unknown, fallback: string): string {
  return cause instanceof Error ? cause.message : fallback;
}

export type ProviderConnectionLifecycle =
  | "validating"
  | "provisioning"
  | "ready"
  | "rotating"
  | "retryable_failure"
  | "capacity_blocked"
  | "revocation_pending"
  | "disconnected"
  | "paused";

export function isActiveProviderConnectionLifecycle(lifecycle: ProviderConnectionLifecycle): boolean {
  return lifecycle === "validating"
    || lifecycle === "provisioning"
    || lifecycle === "ready"
    || lifecycle === "rotating"
    || lifecycle === "paused"
    || lifecycle === "revocation_pending";
}

export function isManagedAiUnavailableError(cause: unknown): boolean {
  return cause instanceof AccountRequestError
    && (
      cause.code === "MANAGED_AI_DISABLED"
      || cause.code === "MANAGED_AI_PLAN_REQUIRED"
      || cause.code === "MANAGED_AI_PERSONAL_WORKSPACE_REQUIRED"
      || cause.code === "MANAGED_AI_PROJECT_DENIED"
    );
}

export interface ManagedAiModel {
  id: string;
  name: string;
  supportsJson: boolean;
  restriction?: string;
  retiringAt?: string;
}

export interface ProviderConnectionProject {
  id: string;
  name: string;
  paused: boolean;
}

export interface ProviderConnection {
  id: string;
  provider: AiProvider;
  lifecycle: ProviderConnectionLifecycle;
  version: number;
  compatibleModels: ManagedAiModel[];
  affectedProjects: ProviderConnectionProject[];
  updatedAt: string;
  message?: string;
}

export interface ProviderConnectionDisconnectImpact {
  confirmationToken: string;
  connectionVersion: number;
  expiresAt: string;
  affectedProjects: ProviderConnectionProject[];
}

export interface ProjectAiRunUsage {
  committed: number;
  remaining: number;
  periodEnd: string;
}

export interface ProjectAiSettings {
  configured: boolean;
  connectionId?: string;
  provider?: AiProvider;
  model?: string;
  compatibleModels?: ManagedAiModel[];
  monthlyRunCap?: number;
  usage?: ProjectAiRunUsage;
  policyVersion?: number;
  paused?: boolean;
  pauseReason?: string;
  updatedAt?: string;
}

export type ProjectCustomHttpStatus =
  | "unavailable"
  | "no_manifest"
  | "invalid"
  | "missing_credentials"
  | "ready"
  | "active"
  | "stale"
  | "disabled";

export interface ProjectCustomHttpConnection {
  alias: string;
  status: "missing" | "active" | "disabled";
  fingerprint?: string;
  generation: number;
  externallyManaged?: boolean;
}

export type ProjectApiDiscoveryDraftStatus =
  | "review_required"
  | "blocked"
  | "ready"
  | "active"
  | "failed"
  | "superseded"
  | "discarded";

export type ProjectApiDiscoveryAcknowledgement = "mutation" | "low_confidence";

export interface ProjectApiDiscoveryFinding {
  id: string;
  file: string;
  span: { start: number; length: number };
  mechanism: "fetch" | "request" | "axios";
  locus: "browser" | "server" | "ambiguous";
  confidence: "high" | "unknown";
  risk: "read" | "mutation" | "unknown";
  request: {
    origin?: string;
    path?: string;
    method: string;
    query: string[];
    headers: string[];
    body?: { mode: "json" | "text" | "unknown"; fields: string[] };
    response: "json" | "text" | "unknown";
  };
  credentials: Array<{
    source: string;
    placement: "header" | "query" | "url" | "unknown";
    transform: "bearer" | "basic" | "direct" | "unknown";
  }>;
  unresolved: string[];
  provenance: Array<{
    field: string;
    file: string;
    span: { start: number; length: number };
    confidence: "high" | "unknown";
    source: "syntax" | "checker";
  }>;
}

export interface ProjectApiDiscoveryDraft {
  draftId: string;
  discoveryVersionId: string;
  workspaceId: string;
  projectId: string;
  revisionId: string;
  sourceDigest: string;
  analyzerVersion: string;
  discoverySchemaVersion: number;
  discoveryDigest: string;
  status: ProjectApiDiscoveryDraftStatus;
  draftVersion: number;
  decisions: Record<string, unknown>;
  acknowledgements: Record<string, ProjectApiDiscoveryAcknowledgement[]>;
  manifestVersionId: string;
  manifestDigest: string;
  confirmationDigest?: string;
  transformDigest?: string;
  transformApprovalDigest?: string;
  terminalOutcome?: "complete_none" | "review_required" | "blocked" | "failed";
  evidence?: {
    complete: boolean;
    credentialSources: Array<{
      source: string;
      placement: "header" | "query" | "url" | "unknown";
      transform: "bearer" | "basic" | "direct" | "unknown";
      file: string;
      span: { start: number; length: number };
      locus: "browser" | "server" | "ambiguous";
      confidence: "high" | "unknown";
    }>;
    findings: ProjectApiDiscoveryFinding[];
    stats: { files: number; sourceBytes: number; astNodes: number };
  };
  blockers?: Array<{
    id: string;
    code: string;
    file?: string;
    span?: { start: number; length: number };
    message: string;
  }>;
}

export interface ProjectCustomHttpSettings {
  eligible: boolean;
  canManage: boolean;
  status: ProjectCustomHttpStatus;
  revisionId?: string;
  manifestDigest?: string;
  activeRevisionId?: string;
  activeManifestDigest?: string;
  manifest?: CustomHttpManifest;
  connections: ProjectCustomHttpConnection[];
  discoveryDraft?: ProjectApiDiscoveryDraft;
  validationErrors?: Array<{ path: string; message: string }>;
}

export interface ProjectPublicApiActionIdentity {
  revisionId: string;
  actionVersionId: string;
  destinationDigest: string;
  requestShapeDigest: string;
  connectionGeneration: number;
  credentialVersionId: string;
  policyVersionId: string;
}

export interface ProjectPublicApiSettings {
  schemaVersion: 1;
  projectId: string;
  role: WorkspaceRole;
  permissions: {
    canEnable: boolean;
    canTakeOffline: boolean;
    canRaiseLimits: boolean;
    canLowerLimits: boolean;
    canApproveMutation: boolean;
    canReactivateSevereTrip: boolean;
  };
  status: {
    state: "unconfigured" | "active" | "challenged" | "throttled" | "automatically_suspended"
      | "owner_disabled" | "credential_disabled" | "operator_disabled";
    publicEnabled: boolean;
    authorityGeneration: number;
    policyVersionId?: string;
    policyVersion?: number;
    revisionId?: string;
    controlGeneration?: number;
    operatorStopped: boolean;
    trip?: {
      scopeKind: "project" | "action" | "connection";
      scopeId: string;
      controlGeneration: number;
      cause: string;
      remediationCode: string;
      requiredRemediationGeneration: number;
      completedRemediationGeneration: number;
      acknowledgementRequired: boolean;
      acknowledged: boolean;
      cooldownUntil: number;
    };
    trips: Array<{
      scopeKind: "project" | "action" | "connection";
      scopeId: string;
      controlGeneration: number;
      cause: string;
      remediationCode: string;
      requiredRemediationGeneration: number;
      completedRemediationGeneration: number;
      acknowledgementRequired: boolean;
      acknowledged: boolean;
      cooldownUntil: number;
    }>;
  };
  blockers: Array<{ code: string; message: string; nextAction: string; ownerRequired: boolean }>;
  requiredOwnerActions: string[];
  actions: Array<{
    id: string;
    key: string;
    risk: PublicCredentialRisk;
    eligible: boolean;
    publicEnabled: boolean;
    approvalStatus: "not_required" | "required" | "current" | "stale" | "ineligible";
    identity?: ProjectPublicApiActionIdentity;
  }>;
  limits: {
    configured: PublicCredentialLimits;
    ceilings: PublicCredentialLimits;
  };
  usage: Array<{
    scope: "project" | "action" | "connection";
    scopeId: string;
    readUsed: number;
    readLimit: number;
    mutationUsed: number;
    mutationLimit: number;
    utcDay: string;
  }>;
  signals: Array<{
    kind: string;
    scope: string;
    count: number;
    threshold: number;
    windowStartedAt: number;
    windowEndsAt: number;
  }>;
  audit: Array<{
    id: string;
    previousState: string;
    nextState: string;
    reasonCode: string;
    actorKind: string;
    occurredAt: string;
    correlationId: string;
  }>;
  nextActions: Array<{
    code: string;
    method: "PATCH";
    ownerRequired: boolean;
  }>;
}

export type ProjectPublicApiOperation =
  | { operation: "review_revision"; expectedAuthorityGeneration: number }
  | { operation: "enable"; expectedAuthorityGeneration: number }
  | { operation: "take_offline"; expectedAuthorityGeneration: number }
  | {
      operation: "set_limits";
      expectedAuthorityGeneration: number;
      limits: PublicCredentialLimits;
    }
  | {
      operation: "approve_mutation";
      expectedAuthorityGeneration: number;
      actionPolicyId: string;
      identity: ProjectPublicApiActionIdentity;
    }
  | {
      operation: "reactivate";
      expectedAuthorityGeneration: number;
      scopeKind: "project" | "action" | "connection";
      scopeId: string;
      expectedControlGeneration: number;
      acknowledgement: true;
      remediationCode: string;
      completedRemediationGeneration: number;
    };

export interface ProjectRevisionHistory {
  schemaVersion: 1;
  projectId: string;
  role: WorkspaceRole;
  permissions: {
    canSelectCandidate: boolean;
    canPublish: boolean;
    humanBrowserApprovalRequired: true;
  };
  preferredCandidate: { snapshotId: string; candidateVersion: number } | null;
  sourceHeads: Array<{
    sourceIdentityId: string;
    sourceLabel: string;
    channel: "browser" | "agent" | "macos" | "external";
    snapshotId: string;
    headVersion: number;
  }>;
  conflict: {
    code: "MULTIPLE_SOURCE_HEADS";
    message: string;
    choices: Array<{ snapshotId: string; sourceIdentityId: string; sourceLabel: string }>;
    nextAction: "SELECT_PREFERRED_CANDIDATE";
  } | null;
  drafts: Array<{
    snapshotId: string;
    sourceIdentityId: string;
    channel: "browser" | "agent" | "macos" | "external";
    sourceLabel: string;
    creatorOrDevice: { kind: "device"; label: string; registeredBy: string }
      | { kind: "external"; label: string };
    sourceDigest: string;
    lifecycle: "ready" | "review_required" | "build_blocked" | "failed" | "superseded";
    analysisState: "complete" | "review_required" | "blocked" | "failed" | "superseded";
    expectedBaseSnapshotId: string | null;
    sourceHeadSnapshotId: string | null;
    sourceHeadVersion: number;
    isSourceHead: boolean;
    isPreferredCandidate: boolean;
    restoreState: "aligned" | "available_source_head" | "would_diverge_from_source_head" | "diverged_from_source_head";
    selectable: boolean;
    buildProvenance: {
      protocolVersion: string;
      omissionRulesVersion: number;
      fileCount: number;
      totalBytes: number;
    };
    createdAt: string;
    nextActions: Array<{
      code: string;
      method: "PATCH" | "REVIEW";
      humanBrowserRequired: boolean;
    }>;
  }>;
  deployments: Array<{
    revisionId: string;
    artifactDigest: string;
    status: "publishing" | "active" | "superseded";
    active: boolean;
    buildState: string;
    failureCode: string | null;
    createdAt: string;
    updatedAt: string;
    restoreControl: {
      available: false;
      reason: "SELECT_EXACT_SOURCE_SNAPSHOT";
      humanBrowserRequired: true;
    };
  }>;
  nextActions: Array<{
    code: string;
    method: "GET" | "REVIEW";
    humanBrowserRequired: boolean;
  }>;
}

export interface ConnectedDevicesState {
  schemaVersion: 1;
  workspaceId: string;
  role: WorkspaceRole;
  permissions: { canViewAll: boolean; canRevoke: boolean };
  devices: Array<{
    id: string;
    label: string;
    status: "active" | "revoked";
    keyFingerprint: string;
    authorityGeneration: number;
    registeredAt: string;
    lastSeenAt: string | null;
    revokedAt: string | null;
    registeredBy: { id: string; name: string };
    scopes: Array<{
      kind: "project" | "workspace";
      projectId: string | null;
      projectName: string | null;
      status: "active" | "revoked";
      generation: number;
    }>;
    canRevoke: boolean;
    nextActions: Array<{ code: "REVOKE_DEVICE"; method: "POST"; ownerRequired: true }>;
  }>;
  nextActions: Array<{ code: string; method: "GET" | "CONTACT"; ownerRequired: boolean }>;
}

export interface BrowserReviewContext {
  workspaceId: string;
  projectId: string;
  sourceIdentityId: string;
  snapshotId: string;
  sourceDigest: string;
  reviewUrl: string;
}

export interface WorkspaceInvitation {
  id: string;
  email: string;
  role: "creator" | "viewer";
  status: "pending" | "accepted" | "revoked" | "expired";
  deliveryStatus: "manual" | "pending" | "sending" | "retrying" | "sent" | "failed";
  deliveryAttemptCount: number;
  deliveryLastAttemptAt?: string | null;
  deliverySentAt?: string | null;
  deliveryFailedAt?: string | null;
  deliveryError?: { code: string; message: string };
  createdAt?: string;
  expiresAt?: string;
}

export interface WorkspaceInvitationPreview {
  workspaceId: string;
  workspaceName: string;
  inviterName: string;
  invitedEmail: string;
  role: "creator" | "viewer";
  status: "pending" | "accepted" | "revoked" | "expired";
  expiresAt: string;
}

export interface ProjectCustomDomain {
  id: string;
  hostname: string;
  status: "pending" | "active" | "blocked";
  hostnameStatus: string;
  dnsStatus: "pending" | "active" | "misconfigured";
  sslStatus?: string;
  lifecycle:
    | "PROVISIONING"
    | "CHECKING_DNS"
    | "ISSUING_SSL"
    | "CONNECTED"
    | "FAILED"
    | "REMOVAL_PENDING"
    | "REMOVAL_FAILED"
    | "REMOVED";
  error?: { code: string; message: string };
  ownershipVerification?: unknown;
  cnameTarget: string;
  createdAt: string;
  updatedAt: string;
}

export interface AppDataRecord {
  collection: string;
  id: string;
  data: unknown;
  createdAt: string;
  updatedAt: string;
}

export interface PlanIntentResult {
  effectivePlan: PlanKey;
  desiredPlan: PlanKey;
  billingQuantity: number;
  billingReady: boolean;
  code: "BILLING_PROVIDER_UNAVAILABLE" | "CHECKOUT_READY";
  checkoutUrl?: string;
}

export class AccountRequestError extends Error {
  constructor(
    message: string,
    readonly code: string,
    readonly status: number,
  ) {
    super(message);
    this.name = "AccountRequestError";
  }
}

async function accountRequest<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    credentials: "same-origin",
    ...init,
    headers: {
      accept: "application/json",
      ...(init?.body ? { "content-type": "application/json" } : {}),
      ...init?.headers,
    },
  });
  const body = await response.json().catch(() => null) as (T & { code?: string; message?: string }) | null;
  if (!response.ok || !body) {
    throw new AccountRequestError(
      body?.message ?? "Doorway could not load this workspace.",
      body?.code ?? "ACCOUNT_REQUEST_FAILED",
      response.status,
    );
  }
  return body;
}

async function accountNoContentRequest(path: string, init: RequestInit): Promise<void> {
  const response = await fetch(path, {
    credentials: "same-origin",
    cache: "no-store",
    referrerPolicy: "no-referrer",
    ...init,
    headers: {
      accept: "application/json",
      ...(init.body ? { "content-type": "application/json" } : {}),
      ...init.headers,
    },
  });
  if (response.ok) return;
  const body = await response.json().catch(() => null) as { code?: string; message?: string } | null;
  throw new AccountRequestError(
    body?.message ?? "Doorway could not complete this device action.",
    body?.code ?? "DEVICE_REQUEST_FAILED",
    response.status,
  );
}

export async function listWorkspaces(): Promise<WorkspaceSummary[]> {
  const result = await accountRequest<{ workspaces: WorkspaceSummary[] }>("/api/account/v1/workspaces");
  return result.workspaces;
}

export function getWorkspaceOverview(workspaceId: string): Promise<WorkspaceOverview> {
  return accountRequest(`/api/account/v1/workspaces/${encodeURIComponent(workspaceId)}`);
}

export function getTeamSkillsLibrary(workspaceId: string): Promise<TeamSkillsLibraryResult> {
  return accountRequest(`/api/account/v1/workspaces/${encodeURIComponent(workspaceId)}/team-skills`, {
    cache: "no-store",
  });
}

export async function uploadTeamSkill(
  workspaceId: string,
  selectedFiles: File[],
): Promise<{ skillId: string; versionId: string; displayName: string }> {
  const normalized = normalizeSelectedSkillFiles(selectedFiles);
  return accountRequest(
    `/api/account/v1/workspaces/${encodeURIComponent(workspaceId)}/team-skills/imports`,
    {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        operationId: crypto.randomUUID(),
        directoryName: normalized.directoryName,
        files: await Promise.all(normalized.files.map(async ({ file, path }) => ({
          path,
          mode: "0644",
          bytesBase64: bytesToBase64(new Uint8Array(await file.arrayBuffer())),
        }))),
      }),
    },
  );
}

export function getTeamSkillHistory(
  workspaceId: string,
  skillId: string,
  cursors: { versionCursor?: number; eventCursor?: number } = {},
): Promise<TeamSkillHistoryResult> {
  const query = new URLSearchParams();
  if (cursors.versionCursor !== undefined) query.set("versionCursor", String(cursors.versionCursor));
  if (cursors.eventCursor !== undefined) query.set("eventCursor", String(cursors.eventCursor));
  const suffix = query.size > 0 ? `?${query}` : "";
  return accountRequest(`/api/account/v1/workspaces/${encodeURIComponent(workspaceId)}/team-skills/${encodeURIComponent(skillId)}${suffix}`, {
    cache: "no-store",
  });
}

export function getTeamSkillVersionComparison(
  workspaceId: string,
  skillId: string,
  versionId: string,
  baseVersionId?: string,
): Promise<TeamSkillVersionComparisonResult> {
  const query = new URLSearchParams();
  if (baseVersionId) query.set("baseVersionId", baseVersionId);
  const suffix = query.size > 0 ? `?${query}` : "";
  return accountRequest(
    `/api/account/v1/workspaces/${encodeURIComponent(workspaceId)}/team-skills/${encodeURIComponent(skillId)}/versions/${encodeURIComponent(versionId)}/compare${suffix}`,
    { cache: "no-store" },
  );
}

export function replaceTeamSkillTags(
  workspaceId: string,
  skillId: string,
  tags: string[],
): Promise<{ workspaceId: string; skillId: string; tags: string[] }> {
  return accountRequest(
    `/api/account/v1/workspaces/${encodeURIComponent(workspaceId)}/team-skills/${encodeURIComponent(skillId)}/tags`,
    {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ tags }),
    },
  );
}

function normalizeSelectedSkillFiles(selectedFiles: File[]): {
  directoryName: string;
  files: Array<{ file: File; path: string }>;
} {
  if (selectedFiles.length === 0) throw new AccountRequestError(
    "Choose the skill folder that contains SKILL.md.",
    "INVALID_SKILL_FOLDER",
    400,
  );
  if (
    selectedFiles.length > TEAM_SKILLS_V1_UPLOAD_MAX_FILES
    || selectedFiles.reduce((total, file) => total + file.size, 0) > TEAM_SKILLS_V1_UPLOAD_MAX_PACKAGE_BYTES
  ) throw new AccountRequestError(
    "Choose a skill with no more than 100 files and 16 MiB.",
    "UPLOAD_SAFE_ENVELOPE_EXCEEDED",
    413,
  );
  const paths = selectedFiles.map((file) => (file.webkitRelativePath || file.name).replace(/^\/+/, ""));
  const roots = new Set(paths.map((path) => path.includes("/") ? path.split("/", 1)[0]! : ""));
  if (roots.size !== 1 || roots.has("")) throw new AccountRequestError(
    "Choose one complete skill folder.",
    "INVALID_SKILL_FOLDER",
    400,
  );
  const directoryName = [...roots][0]!;
  const files = selectedFiles.map((file, index) => ({
    file,
    path: paths[index]!.slice(directoryName.length + 1),
  }));
  if (!files.some(({ path }) => path === "SKILL.md")) throw new AccountRequestError(
    "Choose the skill folder that contains SKILL.md.",
    "MISSING_SKILL_MD",
    400,
  );
  return { directoryName, files };
}

function bytesToBase64(bytes: Uint8Array): string {
  let binary = "";
  const chunkSize = 32 * 1024;
  for (let offset = 0; offset < bytes.byteLength; offset += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(offset, offset + chunkSize));
  }
  return btoa(binary);
}

export function publishTeamSkillVersion(
  workspaceId: string,
  skillId: string,
  versionId: string,
  expectedStateGeneration: number,
): Promise<unknown> {
  return teamSkillMutation(workspaceId, skillId, `versions/${encodeURIComponent(versionId)}:publish`, {
    operationId: crypto.randomUUID(), expectedStateGeneration,
  });
}

export function changeTeamSkillState(
  workspaceId: string,
  skillId: string,
  action: "lock" | "unlock" | "disable",
  expectedStateGeneration: number,
): Promise<unknown> {
  return teamSkillMutation(workspaceId, skillId, `:${action}`, {
    operationId: crypto.randomUUID(), expectedStateGeneration,
  });
}

export function beginTeamSkillOwnerStepUp(
  workspaceId: string,
  skillId: string,
  input: { action: TeamSkillOwnerAction; approvalId?: string; versionId?: string; expectedStateGeneration: number },
): Promise<TeamSkillOwnerStepUpResult> {
  return teamSkillMutation(workspaceId, skillId, "owner-step-up", input);
}

export function completeTeamSkillOwnerAction(
  workspaceId: string,
  skillId: string,
  input: {
    action: TeamSkillOwnerAction;
    approvalId?: string;
    versionId?: string;
    expectedStateGeneration: number;
    capability: string;
  },
): Promise<unknown> {
  const suffix = input.action === "approve"
    ? `approvals/${encodeURIComponent(input.approvalId!)}:approve`
    : input.action === "restore"
      ? `versions/${encodeURIComponent(input.versionId!)}:restore`
      : `:${input.action}`;
  return teamSkillMutation(workspaceId, skillId, suffix, {
    operationId: crypto.randomUUID(), expectedStateGeneration: input.expectedStateGeneration,
    capability: input.capability,
  });
}

function teamSkillMutation<T>(workspaceId: string, skillId: string, suffix: string, body: unknown): Promise<T> {
  return accountRequest(`/api/account/v1/workspaces/${encodeURIComponent(workspaceId)}/team-skills/${encodeURIComponent(skillId)}/${suffix}`, {
    method: "POST", body: JSON.stringify(body),
  });
}

export function updateWorkspaceSettings(
  workspaceId: string,
  input: { name?: string; iconDataUrl?: string | null },
): Promise<{ workspace: WorkspaceOverview["workspace"] }> {
  return accountRequest(`/api/account/v1/workspaces/${encodeURIComponent(workspaceId)}/settings`, {
    method: "PATCH",
    body: JSON.stringify(input),
  });
}

export function deleteWorkspaceProject(
  workspaceId: string,
  projectId: string,
  confirmation: string,
): Promise<{ deleted: true; projectId: string; removedDomains: number }> {
  return accountRequest(
    `/api/account/v1/workspaces/${encodeURIComponent(workspaceId)}/projects/${encodeURIComponent(projectId)}`,
    { method: "DELETE", body: JSON.stringify({ confirmation }) },
  );
}

export function recordPlanIntent(
  workspaceId: string,
  plan: Exclude<PlanKey, "free">,
  cadence: BillingCadence,
  billingQuantity = 1,
): Promise<PlanIntentResult> {
  return accountRequest(`/api/account/v1/workspaces/${encodeURIComponent(workspaceId)}/plan-intent`, {
    method: "POST",
    body: JSON.stringify({ plan, cadence, billingQuantity }),
  });
}

export function openBillingPortal(workspaceId: string): Promise<{ portalUrl: string }> {
  return accountRequest(`/api/account/v1/workspaces/${encodeURIComponent(workspaceId)}/billing-portal`, {
    method: "POST",
  });
}

export function saveTeamDraft(
  workspaceId: string,
  input: TeamDraftInput,
): Promise<{ saved: true }> {
  return accountRequest(`/api/account/v1/workspaces/${encodeURIComponent(workspaceId)}/team-draft`, {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export function getProjectAiSettings(workspaceId: string, projectId: string): Promise<ProjectAiSettings> {
  return accountRequest(aiSettingsPath(workspaceId, projectId));
}

export function saveProjectAiSettings(
  workspaceId: string,
  projectId: string,
  input: { connectionId: string; model: string; monthlyRunCap: number; policyVersion: number },
): Promise<ProjectAiSettings> {
  return accountRequest(aiSettingsPath(workspaceId, projectId), {
    method: "PUT",
    body: JSON.stringify(input),
  });
}

const providerConnectionsPath = "/api/account/v1/provider-connections";

export function listProviderConnections(workspaceId: string): Promise<{ connections: ProviderConnection[] }> {
  return accountRequest(`${providerConnectionsPath}?workspaceId=${encodeURIComponent(workspaceId)}`);
}

export function createProviderConnection(
  input: { workspaceId: string; provider: AiProvider; apiKey: string },
): Promise<{ connection: ProviderConnection }> {
  return accountRequest(providerConnectionsPath, {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export function rotateProviderConnection(
  connectionId: string,
  input: { workspaceId: string; apiKey: string; connectionVersion: number },
): Promise<{ connection: ProviderConnection }> {
  return accountRequest(`${providerConnectionsPath}/${encodeURIComponent(connectionId)}/rotate`, {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export function previewProviderConnectionDisconnect(
  connectionId: string,
  workspaceId: string,
): Promise<ProviderConnectionDisconnectImpact> {
  return accountRequest(`${providerConnectionsPath}/${encodeURIComponent(connectionId)}/disconnect-preview?workspaceId=${encodeURIComponent(workspaceId)}`);
}

export function disconnectProviderConnection(
  connectionId: string,
  input: {
    workspaceId: string;
    confirmationToken: string;
    connectionVersion: number;
    affectedProjectIds: string[];
  },
): Promise<{ disconnected: true; pausedProjectIds: string[] }> {
  return accountRequest(`${providerConnectionsPath}/${encodeURIComponent(connectionId)}`, {
    method: "DELETE",
    body: JSON.stringify(input),
  });
}

export function pauseProjectAiSettings(
  workspaceId: string,
  projectId: string,
): Promise<ProjectAiSettings> {
  return accountRequest(aiSettingsPath(workspaceId, projectId), { method: "DELETE" });
}

export function getProjectCustomHttpSettings(
  workspaceId: string,
  projectId: string,
): Promise<ProjectCustomHttpSettings> {
  return accountRequest(customHttpSettingsPath(workspaceId, projectId));
}

export function getProjectPublicApiSettings(
  workspaceId: string,
  projectId: string,
): Promise<ProjectPublicApiSettings> {
  return accountRequest(publicApiSettingsPath(workspaceId, projectId));
}

export function updateProjectPublicApiSettings(
  workspaceId: string,
  projectId: string,
  operation: ProjectPublicApiOperation,
): Promise<ProjectPublicApiSettings> {
  return accountRequest(publicApiSettingsPath(workspaceId, projectId), {
    method: "PATCH",
    body: JSON.stringify(operation),
  });
}

export function getProjectRevisionHistory(
  workspaceId: string,
  projectId: string,
): Promise<ProjectRevisionHistory> {
  return accountRequest(revisionHistoryPath(workspaceId, projectId), { cache: "no-store" });
}

export function selectProjectSourceCandidate(
  workspaceId: string,
  projectId: string,
  input: { snapshotId: string; expectedCandidateVersion: number },
): Promise<ProjectRevisionHistory> {
  return accountRequest(revisionHistoryPath(workspaceId, projectId), {
    method: "PATCH",
    cache: "no-store",
    body: JSON.stringify({ operation: "select_candidate", ...input }),
  });
}

export function publishProjectSourceCandidate(
  workspaceId: string,
  projectId: string,
  input: { snapshotId: string; expectedCandidateVersion: number },
): Promise<ProjectRevisionHistory & {
  published: {
    projectId: string;
    snapshotId: string;
    revisionId: string;
    url: string;
    mode: string;
    requiresApiReview: boolean;
  };
}> {
  return accountRequest(revisionHistoryPath(workspaceId, projectId), {
    method: "PATCH",
    cache: "no-store",
    body: JSON.stringify({ operation: "publish_candidate", ...input }),
  });
}

export function listConnectedDevices(workspaceId: string): Promise<ConnectedDevicesState> {
  return accountRequest(`/api/account/v1/workspaces/${encodeURIComponent(workspaceId)}/devices`, { cache: "no-store" });
}

export function revokeConnectedDevice(workspaceId: string, deviceId: string): Promise<void> {
  return accountNoContentRequest(
    `/api/device/v1/workspaces/${encodeURIComponent(workspaceId)}/devices/${encodeURIComponent(deviceId)}/revoke`,
    { method: "POST" },
  );
}

export function approveDeviceLink(input: {
  linkId: string;
  approvalCode: string;
  scope: "project" | "workspace";
}): Promise<void> {
  return accountNoContentRequest(`/api/device/v1/links/${encodeURIComponent(input.linkId)}/approve`, {
    method: "POST",
    body: JSON.stringify({ approvalCode: input.approvalCode, scope: input.scope }),
  });
}

export function approveDeviceKeyRotation(input: { rotationId: string; approvalCode: string }): Promise<void> {
  return accountNoContentRequest(`/api/device/v1/rotations/${encodeURIComponent(input.rotationId)}/approve`, {
    method: "POST",
    body: JSON.stringify({ approvalCode: input.approvalCode }),
  });
}

export function consumeBrowserReviewHandoff(handoff: string): Promise<BrowserReviewContext> {
  return accountRequest("/api/device/v1/handoffs/consume", {
    method: "POST",
    cache: "no-store",
    referrerPolicy: "no-referrer",
    body: JSON.stringify({ handoff }),
  });
}

export function getProjectApiDiscovery(
  workspaceId: string,
  projectId: string,
): Promise<ProjectCustomHttpSettings> {
  return accountRequest(customHttpSettingsPath(workspaceId, projectId));
}

export function updateProjectApiDiscovery(
  workspaceId: string,
  projectId: string,
  input: {
    draftId: string;
    expectedDraftVersion: number;
    decisions: Record<string, unknown>;
    acknowledgements: Record<string, ProjectApiDiscoveryAcknowledgement[]>;
    transformDigest?: string;
    transformApprovalDigest?: string;
  },
): Promise<ProjectCustomHttpSettings> {
  return accountRequest(customHttpSettingsPath(workspaceId, projectId), {
    method: "POST",
    body: JSON.stringify({ operation: "discovery_update", ...input }),
  });
}

export function confirmProjectApiDiscovery(
  workspaceId: string,
  projectId: string,
  input: { draftId: string; expectedDraftVersion: number },
): Promise<ProjectCustomHttpSettings> {
  return accountRequest(customHttpSettingsPath(workspaceId, projectId), {
    method: "POST",
    body: JSON.stringify({ operation: "discovery_confirm", ...input }),
  });
}

export function activateProjectApiDiscovery(
  workspaceId: string,
  projectId: string,
  input: { draftId: string; expectedDraftVersion: number; agentOperationId?: string },
): Promise<ProjectCustomHttpSettings> {
  return accountRequest(customHttpSettingsPath(workspaceId, projectId), {
    method: "POST",
    body: JSON.stringify({ operation: "discovery_activate", ...input }),
  });
}

export function saveProjectApiDiscoveryCredential(
  workspaceId: string,
  projectId: string,
  input: { alias: string; credential: string; expectedGeneration: number },
): Promise<ProjectCustomHttpSettings> {
  return saveProjectCustomHttpCredential(workspaceId, projectId, input);
}

export function saveProjectCustomHttpManifest(
  workspaceId: string,
  projectId: string,
  manifest: CustomHttpManifest,
): Promise<ProjectCustomHttpSettings> {
  return accountRequest(customHttpSettingsPath(workspaceId, projectId), {
    method: "PUT",
    body: JSON.stringify({ manifest }),
  });
}

export function saveProjectCustomHttpCredential(
  workspaceId: string,
  projectId: string,
  input: { alias: string; credential: string; expectedGeneration: number },
): Promise<ProjectCustomHttpSettings> {
  return accountRequest(customHttpSettingsPath(workspaceId, projectId), {
    method: "POST",
    body: JSON.stringify({ operation: "credential", ...input }),
  });
}

export function activateProjectCustomHttp(
  workspaceId: string,
  projectId: string,
  input: { revisionId: string; manifestDigest: string; agentOperationId?: string },
): Promise<ProjectCustomHttpSettings> {
  return accountRequest(customHttpSettingsPath(workspaceId, projectId), {
    method: "POST",
    body: JSON.stringify({ operation: "activate", ...input }),
  });
}

export function disableProjectCustomHttpConnection(
  workspaceId: string,
  projectId: string,
  input: { alias: string; expectedGeneration: number },
): Promise<ProjectCustomHttpSettings> {
  return accountRequest(customHttpSettingsPath(workspaceId, projectId), {
    method: "POST",
    body: JSON.stringify({ operation: "disable", ...input }),
  });
}

function aiSettingsPath(workspaceId: string, projectId: string): string {
  return `/api/account/v1/workspaces/${encodeURIComponent(workspaceId)}/projects/${encodeURIComponent(projectId)}/ai-settings`;
}

function customHttpSettingsPath(workspaceId: string, projectId: string): string {
  return `/api/account/v1/workspaces/${encodeURIComponent(workspaceId)}/projects/${encodeURIComponent(projectId)}/custom-http`;
}

function publicApiSettingsPath(workspaceId: string, projectId: string): string {
  return `/api/account/v1/workspaces/${encodeURIComponent(workspaceId)}/projects/${encodeURIComponent(projectId)}/public-api`;
}

function revisionHistoryPath(workspaceId: string, projectId: string): string {
  return `/api/account/v1/workspaces/${encodeURIComponent(workspaceId)}/projects/${encodeURIComponent(projectId)}/revisions`;
}

export async function listWorkspaceInvitations(workspaceId: string): Promise<WorkspaceInvitation[]> {
  const result = await accountRequest<{ invitations: WorkspaceInvitation[] }>(
    `/api/account/v1/workspaces/${encodeURIComponent(workspaceId)}/invitations`,
  );
  return result.invitations;
}

export function createWorkspaceInvitation(
  workspaceId: string,
  input: { email: string; role: "creator" | "viewer" },
): Promise<{ invitation: WorkspaceInvitation; inviteUrl: string; delivery: "queued" | "manual" }> {
  return accountRequest(`/api/account/v1/workspaces/${encodeURIComponent(workspaceId)}/invitations`, {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export function revokeWorkspaceInvitation(workspaceId: string, invitationId: string): Promise<{ revoked: boolean }> {
  return accountRequest(
    `/api/account/v1/workspaces/${encodeURIComponent(workspaceId)}/invitations/${encodeURIComponent(invitationId)}/revoke`,
    { method: "POST" },
  );
}

export function retryWorkspaceInvitation(
  workspaceId: string,
  invitationId: string,
): Promise<{ retried: true; deliveryStatus: "retrying" }> {
  return accountRequest(
    `/api/account/v1/workspaces/${encodeURIComponent(workspaceId)}/invitations/${encodeURIComponent(invitationId)}/retry`,
    { method: "POST" },
  );
}

export function acceptWorkspaceInvitation(token: string): Promise<{ accepted: true; workspaceId: string }> {
  return accountRequest("/api/account/v1/invitations/accept", {
    method: "POST",
    body: JSON.stringify({ token }),
  });
}

export function previewWorkspaceInvitation(token: string): Promise<WorkspaceInvitationPreview> {
  return accountRequest("/api/account/v1/invitations/preview", {
    method: "POST",
    body: JSON.stringify({ token }),
  });
}

export function updateWorkspaceMemberRole(
  workspaceId: string,
  userId: string,
  role: "creator" | "viewer",
): Promise<{ updated: true; role: "creator" | "viewer" }> {
  return accountRequest(memberPath(workspaceId, userId), {
    method: "PATCH",
    body: JSON.stringify({ role }),
  });
}

export function removeWorkspaceMember(workspaceId: string, userId: string): Promise<{ removed: true }> {
  return accountRequest(memberPath(workspaceId, userId), { method: "DELETE" });
}

function memberPath(workspaceId: string, userId: string): string {
  return `/api/account/v1/workspaces/${encodeURIComponent(workspaceId)}/members/${encodeURIComponent(userId)}`;
}

export async function listProjectDomains(workspaceId: string, projectId: string): Promise<ProjectCustomDomain[]> {
  const result = await accountRequest<{ domains: ProjectCustomDomain[] }>(domainPath(workspaceId, projectId));
  return result.domains;
}

export function connectProjectDomain(
  workspaceId: string,
  projectId: string,
  hostname: string,
): Promise<{ domain: ProjectCustomDomain }> {
  return accountRequest(domainPath(workspaceId, projectId), {
    method: "POST",
    body: JSON.stringify({ hostname }),
  });
}

export function removeProjectDomain(workspaceId: string, projectId: string, domainId: string): Promise<{ removed: boolean }> {
  return accountRequest(`${domainPath(workspaceId, projectId)}/${encodeURIComponent(domainId)}`, { method: "DELETE" });
}

export async function inspectProjectDomain(
  workspaceId: string,
  projectId: string,
  domainId: string,
): Promise<ProjectCustomDomain> {
  const result = await accountRequest<{ domain: ProjectCustomDomain }>(
    `${domainPath(workspaceId, projectId)}/${encodeURIComponent(domainId)}`,
  );
  return result.domain;
}

export async function retryProjectDomain(
  workspaceId: string,
  projectId: string,
  domainId: string,
): Promise<{ outcome: "updated"; domain: ProjectCustomDomain } | { outcome: "removed" }> {
  return accountRequest(
    `${domainPath(workspaceId, projectId)}/${encodeURIComponent(domainId)}/retry`,
    { method: "POST" },
  );
}

function domainPath(workspaceId: string, projectId: string): string {
  return `/api/account/v1/workspaces/${encodeURIComponent(workspaceId)}/projects/${encodeURIComponent(projectId)}/domains`;
}

export function startTeamAppAccess(projectId: string, returnUrl: string): Promise<{ redirectUrl: string }> {
  const query = new URLSearchParams({ project: projectId, return: returnUrl });
  return accountRequest(`/api/account/v1/app-access?${query.toString()}`);
}

export async function browseProjectData(workspaceId: string, projectId: string): Promise<AppDataRecord[]> {
  const result = await accountRequest<{ records: AppDataRecord[] }>(dataPath(workspaceId, projectId));
  return result.records;
}

export function restoreProjectData(
  workspaceId: string,
  projectId: string,
  backup: unknown,
): Promise<{ restored: number; backupId: string }> {
  return accountRequest(`${dataPath(workspaceId, projectId)}/restore`, {
    method: "POST",
    body: JSON.stringify(backup),
  });
}

export function projectDataExportUrl(workspaceId: string, projectId: string): string {
  return `${dataPath(workspaceId, projectId)}/export`;
}

function dataPath(workspaceId: string, projectId: string): string {
  return `/api/account/v1/workspaces/${encodeURIComponent(workspaceId)}/projects/${encodeURIComponent(projectId)}/data`;
}
